/**
 * Kanban do setor: "Fila do setor" + um quadro por subsetor, cada um com os
 * seus postos (quem trabalha ali aparece no cabeçalho do posto, com o anel
 * do que está fazendo). Arrastar um cartão para outro posto/subsetor chama
 * `onSoltar(item, grupo)`; a tela decide o que gravar (ambiente, lote ou
 * tarefa). Sem scroll lateral: as colunas quebram linha quando não cabem.
 */
import React from "react";
import { createPortal } from "react-dom";
import { LayoutGroup } from "framer-motion";
import { FiPlus } from "react-icons/fi";

import { plural } from "../../../utils/dinheiro";
import {
  Avatares,
  BotaoIcone,
  CabecaColuna,
  CabecaGrupo,
  Cartao,
  Coluna,
  CorpoColuna,
  Fantasma,
  Grupo,
  ListaCartoes,
  Quadro,
} from "./styles";
import { AvatarPessoa, PillStatus } from "./ui";
import useArrastarCartoes from "./useArrastarCartoes";

// Acima disso o cartão não desliza (medir centenas por render custa caro).
const MAX_CARTOES_ANIMADOS = 150;
const MAX_AVATARES_POSTO = 4;

function totalDaColuna(coluna, grupos) {
  return coluna.grupos.reduce((soma, g) => soma + (grupos.get(g.id)?.length || 0), 0);
}

function atrasadosDaColuna(coluna, grupos, eAtrasado) {
  return coluna.grupos.reduce((soma, g) => soma + (grupos.get(g.id) || []).filter(eAtrasado).length, 0);
}

function PessoasDoPosto({ posto, indice, atividades }) {
  const pessoas = (posto?.colaborador_ids || []).map((id) => indice.colaboradores.get(id)).filter(Boolean);
  if (!pessoas.length) return null;
  return (
    <Avatares title={pessoas.map((p) => `${p.nome}: ${atividades.get(p.id)?.texto || "sem atividade"}`).join("\n")}>
      {pessoas.slice(0, MAX_AVATARES_POSTO).map((p) => {
        const atividade = atividades.get(p.id);
        return (
          <AvatarPessoa
            key={p.id}
            nome={p.nome}
            tamanho={24}
            tom={atividade?.tom || "neutral"}
            trabalhando={atividade?.estado === "trabalhando"}
          />
        );
      })}
    </Avatares>
  );
}

function vazioDoGrupo(grupo, arrastando) {
  if (arrastando) return "Solte aqui";
  if (grupo.posto) return "Posto livre";
  return grupo.subsetorId ? "Nada sem posto" : "Fila vazia";
}

/**
 * Props:
 * - colunas (modelo.montarColunas), grupos: Map<idDoGrupo, itens>
 * - chaveDo(item), rotuloDo(item) (nome acessível do cartão)
 * - estadoDo(item) → {atrasado, encerrado}
 * - renderCartao(item), podeArrastar(item), onSoltar(item, grupo), onAbrir(item)
 * - mudou(item) → realça o cartão que acabou de mudar
 * - indice (combinar.indexarEstado), atividades: Map<colaboradorId, atividade>
 * - onNovaTarefa(grupo) (sem = sem botão "+")
 */
export default function Kanban({
  colunas,
  grupos,
  chaveDo,
  rotuloDo,
  estadoDo,
  renderCartao,
  podeArrastar,
  onSoltar,
  onAbrir,
  mudou,
  indice,
  atividades,
  onNovaTarefa,
  animar = true,
}) {
  const grupoPorId = new Map(colunas.flatMap((c) => c.grupos.map((g) => [g.id, g])));
  const { arraste, registrarArea, propsDoCartao, registrarFantasma } = useArrastarCartoes({
    habilitado: true,
    onSoltar: (item, idDoGrupo) => {
      const grupo = grupoPorId.get(idDoGrupo);
      if (grupo) onSoltar(item, grupo);
    },
  });
  const totalCartoes = [...grupos.values()].reduce((soma, itens) => soma + itens.length, 0);
  const deslizar = animar && totalCartoes <= MAX_CARTOES_ANIMADOS;

  const cartao = (item, grupo, { fantasma = false } = {}) => {
    const chave = chaveDo(item);
    const estado = estadoDo(item);
    const arrastavel = !fantasma && podeArrastar(item);
    const abrir = () => onAbrir(item);
    return (
      <Cartao
        key={chave}
        layout={deslizar && !fantasma ? "position" : false}
        layoutId={deslizar && !fantasma ? chave : undefined}
        transition={{ type: "spring", stiffness: 520, damping: 42, mass: 0.7 }}
        role={fantasma ? undefined : "button"}
        tabIndex={fantasma ? undefined : 0}
        aria-label={fantasma ? undefined : rotuloDo(item)}
        data-cartao={chave}
        data-arrastavel={arrastavel ? "sim" : undefined}
        data-atrasado={estado.atrasado ? "sim" : undefined}
        data-encerrado={estado.encerrado ? "sim" : undefined}
        data-origem={!fantasma && arraste?.chave === chave ? "sim" : undefined}
        data-mudou={!fantasma && mudou(item) ? "sim" : undefined}
        onClick={fantasma ? undefined : abrir}
        onKeyDown={
          fantasma
            ? undefined
            : (e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  abrir();
                }
              }
        }
        {...(arrastavel ? propsDoCartao(item, grupo.id, chave) : {})}
      >
        {renderCartao(item)}
      </Cartao>
    );
  };

  const eAtrasado = (item) => estadoDo(item).atrasado;

  return (
    <LayoutGroup>
      <Quadro $colunas={colunas.length} aria-label="Kanban do setor">
        {colunas.map((coluna) => {
          const total = totalDaColuna(coluna, grupos);
          const atrasados = atrasadosDaColuna(coluna, grupos, eAtrasado);
          return (
            <Coluna key={coluna.id} aria-label={coluna.titulo} data-inativo={coluna.inativo ? "sim" : undefined}>
              <CabecaColuna>
                <span className="titulo" title={coluna.titulo}>{coluna.titulo}</span>
                {coluna.inativo && <PillStatus ponto={false}>Desativado</PillStatus>}
                {atrasados > 0 && (
                  <PillStatus tom="danger">{plural(atrasados, "atrasado", "atrasados")}</PillStatus>
                )}
                <span className="n" aria-label={`${total} na coluna`}>{total}</span>
                {onNovaTarefa && (
                  <BotaoIcone
                    type="button"
                    title={`Nova tarefa em ${coluna.titulo}`}
                    aria-label={`Nova tarefa em ${coluna.titulo}`}
                    onClick={() => onNovaTarefa(coluna.grupos[0])}
                  >
                    <FiPlus size={16} aria-hidden="true" />
                  </BotaoIcone>
                )}
              </CabecaColuna>
              <CorpoColuna>
                {coluna.grupos.map((grupo) => {
                  const itens = grupos.get(grupo.id) || [];
                  const rotulo = `${coluna.titulo} › ${grupo.titulo}`;
                  // Grupo "sem posto" vazio numa coluna com postos só aparece
                  // durante o arraste (é destino para tirar do posto).
                  const ocultar =
                    !arraste && !grupo.posto && itens.length === 0 && coluna.grupos.length > 1;
                  if (ocultar) return null;
                  return (
                    <Grupo
                      key={grupo.id}
                      ref={registrarArea(grupo.id)}
                      role="region"
                      aria-label={rotulo}
                      data-posto={grupo.posto ? "sim" : undefined}
                      data-arrastando={arraste ? "sim" : undefined}
                      data-sobre={arraste?.sobre === grupo.id && arraste.origem !== grupo.id ? "sim" : undefined}
                    >
                      {(grupo.posto || coluna.grupos.length > 1) && (
                        <CabecaGrupo>
                          <span className="titulo" title={grupo.titulo}>{grupo.titulo}</span>
                          {grupo.inativo && <PillStatus ponto={false}>Desativado</PillStatus>}
                          {grupo.posto && (
                            <PessoasDoPosto posto={grupo.posto} indice={indice} atividades={atividades} />
                          )}
                          <span className="n">{itens.length}</span>
                        </CabecaGrupo>
                      )}
                      {itens.length > 0 ? (
                        <ListaCartoes>{itens.map((item) => cartao(item, grupo))}</ListaCartoes>
                      ) : (
                        <div className="vazio">{vazioDoGrupo(grupo, Boolean(arraste))}</div>
                      )}
                    </Grupo>
                  );
                })}
              </CorpoColuna>
            </Coluna>
          );
        })}
      </Quadro>
      {arraste &&
        createPortal(
          <Fantasma ref={registrarFantasma} style={{ width: arraste.largura }} aria-hidden="true">
            {cartao(arraste.item, { id: arraste.origem }, { fantasma: true })}
          </Fantasma>,
          document.body
        )}
    </LayoutGroup>
  );
}
